// src/pages/MasterSyncPage.js
//
// Master inventory sheet sync UI — Phase 2.
// Backed by /api/import/master/{preview,apply,runs}.
//
// Flow:
//   1. Pick the .xlsx → Preview Changes (server returns updates / inserts /
//      conflicts plan, no writes).
//   2. Review summary tiles + expandable per-unit diffs. Per-unit checkboxes
//      let the operator skip individual rows; per-field "keep DB" override
//      isn't surfaced yet (apply respects the server's `decision` field
//      computed from per-field provenance).
//   3. Apply — same file is re-uploaded so the server re-parses + re-matches
//      against the freshest DB state and writes inside a single sync run.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../scripts/api";

const FIELD_LABELS = {
  rowo: "RO/WO #",
  stock_po: "Stock # / PO",
  sna_customer_po: "SNA Customer PO",
  sna_body_chassis_po: "Body Chassis PO",
  customer: "Customer",
  color_code: "Color",
  body_make_model: "Body Make/Model",
  chassis_make_model: "Chassis Make/Model",
  body_vin: "Body VIN",
  chassis_vin: "Chassis VIN",
  factory_wo: "Factory WO",
  factory_production_so_order: "Factory Prod SO",
  sent_so: "Sent SO",
  sign_so: "Sign SO",
  revd_eng: "Rev'd Eng",
  customer_po_date: "Customer PO Date",
  factory_order_date: "Factory Order Date",
  ready_at_factory: "Ready @ Factory",
  arrive_sna: "Arrive SNA",
  to_customer: "To Customer",
  unit_location: "Location",
  chassis_build_date: "Chassis Build",
  chassis_arrive_eta: "Chassis Arrive ETA",
};

function fmt(v) {
  if (v === null || v === undefined || v === "") return <span className="muted">—</span>;
  const s = String(v);
  return s.length > 60 ? s.slice(0, 57) + "…" : s;
}

export default function MasterSyncPage() {
  const nav = useNavigate();
  const [file, setFile]         = useState(null);
  const [preview, setPreview]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [applying, setApplying] = useState(false);
  const [err, setErr]           = useState("");
  const [skipIds, setSkipIds]   = useState(new Set());      // DB row IDs operator chose to skip
  const [insertDisabled, setInsertDisabled] = useState(false);
  const [runs, setRuns]         = useState([]);
  const [filter, setFilter]     = useState("all");          // 'all' | 'updates' | 'inserts' | 'conflicts'
  const [expanded, setExpanded] = useState(new Set());

  const [duplicates, setDuplicates] = useState(null);
  const [dupsLoading, setDupsLoading] = useState(false);

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await api.get("/import/master/runs");
      setRuns(data?.runs || []);
    } catch (_) { /* ignore */ }
  }, []);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  const loadDuplicates = useCallback(async () => {
    setDupsLoading(true);
    try {
      const { data } = await api.get("/import/master/duplicates");
      setDuplicates(data);
    } catch (e) {
      window.alert("Failed to load duplicates: " + (e?.response?.data?.error || e.message));
    } finally {
      setDupsLoading(false);
    }
  }, []);

  const rollbackRun = async (run) => {
    if (!run?.id) return;
    const insertedCount = run.inserted_count ?? run.stats?.insertedRows ?? 0;
    if (!insertedCount) {
      window.alert(`Run #${run.id} inserted 0 records — nothing to roll back.`);
      return;
    }
    if (!window.confirm(
      `Roll back sync run #${run.id}?\n\n` +
      `This will DELETE ${insertedCount} inventory records that were inserted by this run, ` +
      `along with their unit_components. Updates this run made to existing records will NOT be reverted (the old values aren't recoverable).\n\n` +
      `This action cannot be undone.`
    )) return;
    try {
      const { data } = await api.post(`/import/master/runs/${run.id}/rollback`);
      window.alert(
        `Rollback complete.\n\n` +
        `Deleted ${data.deletedRows} inventory rows + ${data.componentsDeleted} components.`
      );
      await loadRuns();
      if (duplicates) await loadDuplicates();
    } catch (e) {
      window.alert("Rollback failed: " + (e?.response?.data?.error || e.message));
    }
  };

  const deleteRecord = async (id, label) => {
    if (!window.confirm(
      `Delete inventory record #${id} (${label})?\n\nThis is a HARD delete — the row and its components are gone forever.`
    )) return;
    try {
      await api.delete(`/all_inventory/${id}`);
      await loadDuplicates();
    } catch (e) {
      window.alert("Delete failed: " + (e?.response?.data?.error || e.message));
    }
  };

  // Merge record `loserId` into `winnerId` — winner keeps its own values
  // wherever it has them, only fills in blanks from the loser; components
  // and references are moved across; loser is deleted.
  const mergeInto = async (loserId, loserLabel, winnerId, winnerLabel) => {
    if (!window.confirm(
      `Merge record #${loserId} (${loserLabel}) INTO #${winnerId} (${winnerLabel})?\n\n` +
      `Winner #${winnerId} keeps its own values. Empty fields on the winner will be filled in from the loser. ` +
      `Components, moves, breakdowns, expenses, PDIs, notes and images are moved to the winner.\n\n` +
      `Record #${loserId} will be DELETED. This cannot be undone.`
    )) return;
    try {
      const { data } = await api.post(`/import/master/merge`, {
        winner_id: winnerId,
        loser_id:  loserId,
      });
      const relocSummary = Object.keys(data.relocated || {}).length
        ? "\nMoved: " + Object.entries(data.relocated).map(([t, n]) => `${t}=${n}`).join(", ")
        : "";
      window.alert(
        `Merged.\n\n` +
        `Winner #${data.winner_id} got ${data.merged_fields} new field${data.merged_fields === 1 ? "" : "s"}` +
        (data.merged_field_names?.length ? ` (${data.merged_field_names.slice(0, 6).join(", ")}${data.merged_field_names.length > 6 ? ", …" : ""})` : "") +
        `.\nComponents moved: ${data.components_moved}\nMoves moved: ${data.moves_moved}` +
        relocSummary
      );
      await loadDuplicates();
    } catch (e) {
      window.alert("Merge failed: " + (e?.response?.data?.error || e.message));
    }
  };

  const doPreview = async () => {
    setErr(""); setPreview(null); setSkipIds(new Set()); setExpanded(new Set());
    if (!file) return setErr("Pick a .xlsx file first.");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/master/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setPreview(data);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const doApply = async () => {
    if (!preview || !file) return;
    if (!window.confirm(
      `Apply sync now?\n\n` +
      `${preview.stats.willUpdate} updates · ${preview.stats.willInsert} inserts · ${preview.stats.conflicts} conflicts skipped.\n` +
      `Skipped manually: ${skipIds.size}\n` +
      `${insertDisabled ? "Inserts disabled — only updates will run." : ""}`
    )) return;
    setApplying(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("skip_unit_ids", JSON.stringify(Array.from(skipIds)));
      fd.append("insert_disabled", String(!!insertDisabled));
      const { data } = await api.post("/import/master/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 1800000,
      });
      window.alert(
        `Sync run #${data.run_id}\n\n` +
        `Updated rows: ${data.stats.updatedRows}\n` +
        `Updated fields: ${data.stats.updatedFields}\n` +
        `Inserted rows: ${data.stats.insertedRows}\n` +
        `Components inserted: ${data.stats.componentsInserted ?? 0}\n` +
        `Components updated:  ${data.stats.componentsUpdated ?? 0}\n` +
        `Skipped rows: ${data.stats.skippedRows}\n` +
        `Conflicts (need manual): ${data.stats.conflicts}\n` +
        (data.stats.errorCount ? `Errors: ${data.stats.errorCount} (see run log)\n` : "")
      );
      // Re-preview against the now-updated DB so the operator sees a clean slate.
      await doPreview();
      await loadRuns();
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const toggleSkip = (id) => {
    setSkipIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleExpand = (key) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const visibleUpdates = useMemo(() => {
    if (!preview) return [];
    if (filter !== "all" && filter !== "updates") return [];
    return preview.updates;
  }, [preview, filter]);
  const visibleInserts = useMemo(() => {
    if (!preview) return [];
    if (filter !== "all" && filter !== "inserts") return [];
    return preview.inserts;
  }, [preview, filter]);
  const visibleConflicts = useMemo(() => {
    if (!preview) return [];
    if (filter !== "all" && filter !== "conflicts") return [];
    return preview.conflicts;
  }, [preview, filter]);

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>Master Sheet Sync</h2>
          <div className="muted" style={{ marginTop: 4, maxWidth: 720 }}>
            Upload the master inventory workbook → preview the proposed update / insert / conflict
            plan → apply. Body and chassis rows under one RO/WO are merged into a single record;
            accessories become unit_components. Fields edited in the IMS since the last sync are
            preserved (the sheet doesn't overwrite them).
          </div>
        </div>
        <button className="btn-outline" onClick={() => nav("/home")}>Home</button>
      </div>

      {/* Upload + actions */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            className="input"
            type="file"
            accept=".xlsx"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setErr(""); }}
            style={{ flex: "1 1 320px", maxWidth: 480 }}
          />
          <button className="btn" onClick={doPreview} disabled={loading || !file}>
            {loading ? "Parsing & matching…" : "Preview Changes"}
          </button>
          <button
            className="btn-outline"
            onClick={doApply}
            disabled={applying || !preview || (preview.stats.willUpdate === 0 && preview.stats.willInsert === 0)}
            style={{ background: "#16a34a", color: "#fff", borderColor: "#15803d" }}
          >
            {applying ? "Applying…" : "Apply"}
          </button>
          <label className="row" style={{ gap: 6, alignItems: "center", fontSize: 13 }}>
            <input type="checkbox" checked={insertDisabled} onChange={(e) => setInsertDisabled(e.target.checked)} />
            Skip inserts (updates only)
          </label>
        </div>
        {err && <div style={{ marginTop: 10, color: "#dc2626", fontWeight: 600 }}>{err}</div>}
      </div>

      {preview && (
        <>
          {/* Summary tiles */}
          <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <Tile label="Sheet rows parsed" value={preview.stats.parserStats?.totalRows ?? 0} sub={`${preview.stats.totalParsed} merged units`} />
            <Tile label="Will update" value={preview.stats.willUpdate} color="#1d4ed8" />
            <Tile label="Will insert" value={preview.stats.willInsert} color="#16a34a" />
            <Tile label="Skipped (DB edited)" value={preview.stats.willSkip} color="#d97706" />
            <Tile label="Conflicts" value={preview.stats.conflicts} color="#dc2626" sub="multiple DB candidates" />
            <Tile label="Warnings" value={preview.warningCount} color="#92400e" sub="duplicate body/chassis etc" />
          </div>

          {preview.warningCount > 0 && (
            <div style={{ marginTop: 10, background: "#fef3c7", border: "1px solid #fde68a", color: "#92400e", borderRadius: 12, padding: 14 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>
                ⚠ {preview.warningCount} parser warnings — these don't block the sync but worth a glance
              </div>
              <div style={{ display: "grid", gap: 4, fontSize: 12, fontFamily: "monospace", maxHeight: 200, overflowY: "auto", color: "#78350f" }}>
                {preview.warnings.slice(0, 30).map((w, i) => (
                  <div key={i}>{w.sheet} row {w.row}: {w.reason}</div>
                ))}
                {preview.warnings.length > 30 && <div>…and {preview.warnings.length - 30} more</div>}
              </div>
            </div>
          )}

          {/* Filter pills */}
          <div className="row" style={{ gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            {["all", "updates", "inserts", "conflicts"].map((k) => (
              <button
                key={k}
                className={filter === k ? "btn" : "btn-outline"}
                onClick={() => setFilter(k)}
                style={{ fontSize: 13 }}
              >
                {k.toUpperCase()} ({k === "all" ? preview.updates.length + preview.inserts.length + preview.conflicts.length
                                  : k === "updates" ? preview.updates.length
                                  : k === "inserts" ? preview.inserts.length
                                  : preview.conflicts.length})
              </button>
            ))}
          </div>

          {/* UPDATEs */}
          {visibleUpdates.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Updates ({visibleUpdates.length})</div>
              <div style={{ display: "grid", gap: 8 }}>
                {visibleUpdates.map((u) => {
                  const key = `u${u.db.id}`;
                  const isExpanded = expanded.has(key);
                  const isSkipped = skipIds.has(u.db.id);
                  const applyFields = u.diff.filter((f) => f.decision === "apply").length;
                  const keepFields  = u.diff.filter((f) => f.decision === "keep_db").length;
                  return (
                    <div key={key} className="card" style={{
                      padding: 10, opacity: isSkipped ? 0.55 : 1,
                      borderLeft: u.decision === "skip" ? "4px solid #d97706" : "4px solid #1d4ed8",
                    }}>
                      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 700 }}>
                            #{u.db.id} · {u.unit.rowo || "(no rowo)"} · {u.unit.customer || u.db.customer || "(no customer)"}
                          </div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            matched by {u.matched_by} · {applyFields} change{applyFields === 1 ? "" : "s"} to apply
                            {keepFields ? ` · ${keepFields} held back (DB edited since last sync)` : ""}
                            {u.unit.component_count ? ` · ${u.unit.component_count} components` : ""}
                          </div>
                        </div>
                        <div className="row" style={{ gap: 6 }}>
                          <button className="btn-outline" onClick={() => toggleExpand(key)} style={{ fontSize: 12 }}>
                            {isExpanded ? "Hide diff" : "Show diff"}
                          </button>
                          <label className="row" style={{ gap: 4, alignItems: "center", fontSize: 12 }}>
                            <input type="checkbox" checked={isSkipped} onChange={() => toggleSkip(u.db.id)} />
                            Skip
                          </label>
                        </div>
                      </div>
                      {isExpanded && (
                        <table style={{ width: "100%", marginTop: 8, fontSize: 13, borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ background: "var(--card-border)", textAlign: "left" }}>
                              <th style={{ padding: 6 }}>Field</th>
                              <th style={{ padding: 6 }}>Current (DB)</th>
                              <th style={{ padding: 6 }}>New (Sheet)</th>
                              <th style={{ padding: 6 }}>Decision</th>
                            </tr>
                          </thead>
                          <tbody>
                            {u.diff.map((f) => (
                              <tr key={f.col} style={{ borderTop: "1px solid var(--card-border)" }}>
                                <td style={{ padding: 6, fontWeight: 700 }}>{FIELD_LABELS[f.col] || f.col}</td>
                                <td style={{ padding: 6 }}>{fmt(f.old)}</td>
                                <td style={{ padding: 6 }}>{fmt(f.new)}</td>
                                <td style={{ padding: 6 }}>
                                  {f.decision === "apply" ? (
                                    <span style={{ color: "#16a34a", fontWeight: 700 }}>→ apply</span>
                                  ) : (
                                    <span style={{ color: "#d97706", fontWeight: 700 }} title={f.reason || ""}>keep DB</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* INSERTs */}
          {visibleInserts.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>New units to insert ({visibleInserts.length})</div>
              <div style={{ display: "grid", gap: 8 }}>
                {visibleInserts.map((ins, idx) => (
                  <div key={idx} className="card" style={{ padding: 10, borderLeft: "4px solid #16a34a" }}>
                    <div style={{ fontWeight: 700 }}>
                      {ins.unit.rowo || "(no rowo)"} · {ins.unit.customer || "(no customer)"}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      color {ins.unit.color_code} · body: {ins.unit.body_make_model || "—"} · chassis: {ins.unit.chassis_make_model || "—"}
                      {ins.unit.component_count > 0 && ` · ${ins.unit.component_count} components`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CONFLICTs */}
          {visibleConflicts.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 800, marginBottom: 8, color: "#dc2626" }}>
                Conflicts — multiple DB candidates ({visibleConflicts.length})
              </div>
              <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                These rows match more than one DB record. Review them in the inventory editor and
                either delete duplicates or fix the rowo / VIN before re-running the sync.
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {visibleConflicts.map((c, idx) => (
                  <div key={idx} className="card" style={{ padding: 10, borderLeft: "4px solid #dc2626" }}>
                    <div style={{ fontWeight: 700 }}>
                      {c.unit.rowo || "(no rowo)"} · {c.unit.customer || "(no customer)"}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      Sheet says: stock {c.unit.stock_po || "—"} · body VIN {c.unit.body_vin || "—"} · chassis VIN {c.unit.chassis_vin || "—"}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, fontFamily: "monospace" }}>
                      DB candidates:
                      {c.candidates.map((cand) => (
                        <div key={cand.id}>
                          · #{cand.id} (matched by {cand.matched_by}) — {cand.customer || "?"} ·
                          rowo={cand.rowo || "—"} stock={cand.stock_po || "—"} body={cand.body_vin || "—"} chassis={cand.chassis_vin || "—"}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Danger zone — rollback + duplicate detection */}
      <div className="card" style={{ marginTop: 14, background: "#fef2f2", border: "1px solid #fca5a5", color: "#7f1d1d" }}>
        <div style={{ fontWeight: 800, color: "#7f1d1d", marginBottom: 6 }}>⚠ Danger Zone</div>
        <div style={{ fontSize: 13, marginBottom: 8, color: "#991b1b" }}>
          Tools to recover from a bad sync. Rollback deletes records inserted by a specific run; the
          duplicates report finds inventory rows that look like duplicates of each other so you can
          investigate and clean up by hand.
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn-outline" onClick={loadDuplicates} disabled={dupsLoading}>
            {dupsLoading ? "Scanning…" : "🔍 Find Duplicates"}
          </button>
        </div>

        {duplicates && (
          <div style={{ marginTop: 12, color: "#7f1d1d" }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>
              Found {duplicates.totals.rowoGroups} groups by RO/WO ·
              {" "}{duplicates.totals.chassisVinGroups} by chassis VIN ·
              {" "}{duplicates.totals.bodyVinGroups} by body VIN
            </div>
            {duplicates.byRowo?.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: "#7f1d1d" }}>By RO/WO (normalised)</div>
                <div style={{ display: "grid", gap: 8, maxHeight: 360, overflowY: "auto" }}>
                  {duplicates.byRowo.map((g) => (
                    <div key={g.norm_rowo} className="card" style={{ padding: 8, background: "#fff", color: "#0b0f19" }}>
                      <div style={{ fontFamily: "monospace", fontWeight: 700, marginBottom: 4 }}>
                        {g.norm_rowo} · {g.dup_count} records
                      </div>
                      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "rgba(0,0,0,0.04)", textAlign: "left" }}>
                            <th style={{ padding: 4 }}>ID</th>
                            <th style={{ padding: 4 }}>RO/WO</th>
                            <th style={{ padding: 4 }}>Stock</th>
                            <th style={{ padding: 4 }}>Customer</th>
                            <th style={{ padding: 4 }}>Body</th>
                            <th style={{ padding: 4 }}>Chassis</th>
                            <th style={{ padding: 4 }}>Synced</th>
                            <th style={{ padding: 4 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(g.records || []).map((r) => {
                            const others = (g.records || []).filter((x) => x.id !== r.id);
                            const labelOf = (rec) => `${rec.rowo || "?"} · ${rec.customer || "?"}`;
                            return (
                              <tr key={r.id} style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                                <td style={{ padding: 4 }}>#{r.id}</td>
                                <td style={{ padding: 4 }}>{r.rowo || "—"}</td>
                                <td style={{ padding: 4 }}>{r.stock_po || "—"}</td>
                                <td style={{ padding: 4, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.customer || "—"}</td>
                                <td style={{ padding: 4, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.body_make_model || "—"}</td>
                                <td style={{ padding: 4, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.chassis_make_model || "—"}</td>
                                <td style={{ padding: 4, fontSize: 11 }}>{String(r.last_sheet_synced_at || "").slice(0, 10)}</td>
                                <td style={{ padding: 4 }}>
                                  <div className="row" style={{ gap: 4, flexWrap: "nowrap" }}>
                                    {others.length > 0 && (
                                      <select
                                        className="input"
                                        style={{ fontSize: 11, padding: "2px 4px", height: 24 }}
                                        defaultValue=""
                                        onChange={(e) => {
                                          const winnerId = Number(e.target.value);
                                          e.target.value = "";   // reset so user can pick again
                                          if (Number.isFinite(winnerId)) {
                                            const winner = others.find((o) => o.id === winnerId);
                                            mergeInto(r.id, labelOf(r), winnerId, winner ? labelOf(winner) : `#${winnerId}`);
                                          }
                                        }}
                                        title="Merge this record into another (picks winner — winner keeps its own data)"
                                      >
                                        <option value="">Merge into…</option>
                                        {others.map((o) => (
                                          <option key={o.id} value={o.id}>#{o.id} · {o.rowo || "?"} · {o.customer || "?"}</option>
                                        ))}
                                      </select>
                                    )}
                                    <button
                                      className="btn-outline"
                                      style={{ fontSize: 11, padding: "2px 6px", color: "#dc2626", borderColor: "#fca5a5" }}
                                      onClick={() => deleteRecord(r.id, labelOf(r))}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent runs */}
      {runs.length > 0 && (
        <div className="card" style={{ marginTop: 14 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Recent sync runs</div>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--card-border)", textAlign: "left" }}>
                <th style={{ padding: 6 }}>#</th>
                <th style={{ padding: 6 }}>When</th>
                <th style={{ padding: 6 }}>Operator</th>
                <th style={{ padding: 6 }}>Source</th>
                <th style={{ padding: 6 }}>Status</th>
                <th style={{ padding: 6 }}>Updates</th>
                <th style={{ padding: 6 }}>Inserts</th>
                <th style={{ padding: 6 }}>Errors</th>
                <th style={{ padding: 6 }}></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const canRollback = (r.status === "completed" || r.status === "completed_with_errors")
                  && (r.inserted_count ?? r.stats?.insertedRows ?? 0) > 0;
                return (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--card-border)" }}>
                    <td style={{ padding: 6 }}>#{r.id}</td>
                    <td style={{ padding: 6 }}>{String(r.started_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td style={{ padding: 6 }}>{r.applied_by_email || "—"}</td>
                    <td style={{ padding: 6, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.source_file || "—"}
                    </td>
                    <td style={{ padding: 6 }}>
                      <span style={{
                        padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700,
                        background: r.status === "completed" ? "#dcfce7"
                                  : r.status === "completed_with_errors" ? "#fef3c7"
                                  : r.status === "rolled_back" ? "#e5e7eb"
                                  : r.status === "failed" ? "#fee2e2" : "#f3f4f6",
                        color: r.status === "completed" ? "#166534"
                             : r.status === "completed_with_errors" ? "#92400e"
                             : r.status === "rolled_back" ? "#4b5563"
                             : r.status === "failed" ? "#b91c1c" : "#374151",
                      }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ padding: 6 }}>{r.stats?.updatedRows ?? "—"}</td>
                    <td style={{ padding: 6 }}>{r.stats?.insertedRows ?? "—"}</td>
                    <td style={{ padding: 6 }}>{r.stats?.errorCount ?? "—"}</td>
                    <td style={{ padding: 6 }}>
                      {canRollback ? (
                        <button
                          className="btn-outline"
                          style={{ fontSize: 11, padding: "2px 8px", color: "#dc2626", borderColor: "#fca5a5" }}
                          onClick={() => rollbackRun(r)}
                          title={`Delete the ${r.inserted_count ?? r.stats?.insertedRows} inventory rows this run inserted`}
                        >
                          Rollback
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, sub, color }) {
  return (
    <div className="card" style={{ flex: "1 1 160px", minWidth: 140, padding: 12 }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 900, marginTop: 4, color: color || undefined }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
